import React from 'react'
import { View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import ToastComponent from '@common/components/Toast'
import { isWeb } from '@common/config/env'
import { HEADER_HEIGHT } from '@common/modules/header/components/Header/styles'
import { SPACING, SPACING_SM, SPACING_TY } from '@common/styles/spacings'

import { useToastBottomInset } from './bottomInsetRegistry'
import { splitAnchor } from './config'
import { getContainerStyle } from './styles'
import { useToastList } from './toastListContext'

/**
 * Rendered through a Portal into the "global" host, which sits inside
 * ThemeProvider — that's why everything theme/inset related lives here and not
 * in ToastProvider, which is mounted above ThemeProvider.
 */
const ToastList = () => {
  const { visible, dismiss, anchor } = useToastList()
  const insets = useSafeAreaInsets()
  const bottomInset = useToastBottomInset()

  const [edge] = splitAnchor(anchor)

  const offsets = {
    // Top-corner anchors float above the header rather than below it.
    top:
      edge === 'top' && anchor !== 'top-center'
        ? insets.top + SPACING
        : insets.top + HEADER_HEIGHT + (isWeb ? 0 : SPACING_TY),
    bottom: insets.bottom + bottomInset + SPACING_SM
  }

  // Newest first when anchored at the top, newest closest to the bottom edge
  // (nearest the thumb) otherwise.
  const ordered = edge === 'top' ? [...visible].reverse() : visible

  return (
    // Toasts are how the wallet reports both success and failure ("transaction
    // submitted", "insufficient balance"). Without a live region they are purely
    // visual and a screen-reader user gets no feedback at all. The region wraps
    // the list rather than each toast, so the announcement fires when a toast is
    // inserted into an already-present container.
    <View
      accessibilityLiveRegion="polite"
      // @ts-ignore-next-line — react-native-web only
      role="status"
      style={getContainerStyle(anchor, offsets)}
    >
      {ordered.map((toast) => (
        <ToastComponent
          {...toast}
          key={toast.id}
          anchor={anchor}
          removeToast={dismiss}
          isTypeLabelHidden={toast.isTypeLabelHidden ?? true}
        />
      ))}
    </View>
  )
}

export default ToastList
